import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Lock, MapPin, NotebookPen, RefreshCcw, Swords, Users, type LucideIcon } from 'lucide-react'
import Header from '@/components/layout/Header'
import StrategyLibrary from '@/components/strategy/StrategyLibrary'
import { fetchMaps as fetchMapCatalog, type MapInfo } from '@/lib/maps'
import { tacticalFilter } from '@/lib/mapView'
import {
  isStrategyKind, sectionFromSlug, sectionPath, type StrategySection,
} from '@/lib/strategyKinds'
import { useAuth, isStrategyTeam } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

/**
 * Strateji sekmesi — üç bölüm, tek sayfa.
 *
 *   Maç Stratejileri   klan maçları için çizilen planlar, yalnızca yönetim açar
 *   Stratejik Noktalar harita başına nokta katmanı, herkes ekler
 *   Taktik Defteri     herkesin paylaştığı açık taktikler
 *
 * Bölümler ayrı sayfalar değil **aynı sayfanın sekmeleri**: üçü de aynı harita
 * kataloğunu, aynı editörü ve aynı tasarım dilini kullanıyor, ve bir bölümden
 * diğerine geçmek sık bir hareket. Yine de her bölümün adresi var
 * (`/strategy/mac`, `/strategy/noktalar`, `/strategy/defter`), çünkü bir
 * bölümün linki paylaşılabilir olmalı.
 *
 * Eski sayfa yalnızca stratejik nokta haritalarını listeliyordu, stratejiler
 * ise sol kenardaki daraltılmış bir liste olarak duruyordu — o kenar çubuğu
 * editörde kaldı (strateji içinde gezinmek için), ama sekmenin ön kapısı artık
 * kitaplık ızgarasıdır.
 */

interface SectionDef {
  id: StrategySection
  icon: LucideIcon
  label: string
  desc: string
  access: string
  /** Bu bölüme yeni satır ekleyebilmek yönetim yetkisi ister mi? */
  staffOnly: boolean
}

// ── Map card ────────────────────────────────────────────────────────────────
/**
 * Harita kartı: oyunun kendi tanıtım görseli (`hero`), altında kısa bir arazi
 * tarifi.
 *
 * **Görselin üstüne harita adı basılmaz.** Ad zaten görselin içinde, sol altta,
 * oyunun kendi tipografisiyle duruyor; ikincisini eklemek aynı adı iki kez
 * yazmak olurdu. Aynı sebeple kadraj **16:9 tam** (`aspect-[16/9]`) ve kırpma
 * yok — daha kısa bir şerit adı keserdi.
 *
 * Görsel **filtresiz** basılır. Kart bir zamanlar tepeden harita görüntüsünü
 * taktiksel filtreyle (gri + eşitlenmiş parlaklık) gösteriyordu, çünkü üç ham
 * harita yan yana birbiriyle yarışıyor ve üstlerindeki yazının kontrastı
 * haritadan haritaya değişiyordu. Tanıtım görsellerinde ikisi de sorun değil:
 * üçü de aynı alacakaranlık paletinde derecelenmiş ve üstlerinde artık yazı
 * yok. Griye indirmek burada sadece görseli ziyan ederdi.
 *
 * Görseli olmayan bir harita için `thumb` eski davranışıyla (taktiksel filtre)
 * devreye girer — yeni bir harita, artwork'ü gelmeden önce de kartını alır.
 */
function MapCard({ map, description, onClick }: {
  map: MapInfo
  description?: string
  onClick: () => void
}) {
  const art = map.hero ?? map.thumb
  const filtered = !map.hero && map.luma

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full flex-col overflow-hidden rounded-sm border border-white/10 bg-white/[0.035] text-left transition-colors hover:border-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-black/40">
        <img
          src={art}
          alt=""
          aria-hidden
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
          style={filtered ? { filter: tacticalFilter(map.luma!) } : undefined}
        />
        {/* Adın oturduğu sol alt köşeyi biraz koyulaştıran perde: görsel
            zaten dereceli, bu yalnızca üç kartın köşesini aynı tona getirir. */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
      </div>

      {description && (
        <p className="p-4 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}
    </button>
  )
}

// ── Strategic points section ────────────────────────────────────────────────
function PointsPicker() {
  const navigate = useNavigate()
  const { T } = useLanguage()
  const [maps, setMaps] = useState<MapInfo[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try { setMaps(await fetchMapCatalog()) } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <p className="flex-1 text-xs text-muted-foreground">{T.strategy.pointsPicker.hint}</p>
        <button
          onClick={load}
          disabled={loading}
          className="rounded-sm border border-white/10 p-2 text-muted-foreground/60 transition-colors hover:text-foreground disabled:opacity-40"
          title={T.strategy.refresh}
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && maps.length === 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="aspect-[16/9] animate-pulse rounded-sm bg-white/5" />
          ))}
        </div>
      )}

      {!loading && maps.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin className="mb-3 h-12 w-12 text-muted-foreground/20" />
          <p className="text-sm text-muted-foreground">{T.strategy.noMapsAvailable}</p>
        </div>
      )}

      {maps.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {maps.map(m => (
            <MapCard
              key={m.name}
              map={m}
              // Katalogdaki `name` dinamik; sabit anahtarlı çeviri nesnesine
              // indekslemek için gevşetiliyor, karşılığı yoksa metin düşer.
              description={(T.strategy.mapDescriptions as Record<string, string | undefined>)[m.name]}
              onClick={() => navigate(`/strategic-points/${m.name}`)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function Strategy() {
  const navigate = useNavigate()
  const { section: slug } = useParams<{ section?: string }>()
  const { T } = useLanguage()
  const { user } = useAuth()
  const active = sectionFromSlug(slug)
  // Strategy team (operators + strategist) may open and build match
  // strategies; publishing one to a match stays operator-only (MatchStrategyCard).
  const staff = isStrategyTeam(user)

  const sections: SectionDef[] = [
    {
      id: 'match',
      icon: Swords,
      label: T.strategy.tabs.match,
      desc: T.strategy.sectionDesc.match,
      access: T.strategy.access.staffOnly,
      staffOnly: true,
    },
    {
      id: 'points',
      icon: MapPin,
      label: T.strategy.tabs.points,
      desc: T.strategy.sectionDesc.points,
      access: T.strategy.access.everyoneOwn,
      staffOnly: false,
    },
    {
      id: 'playbook',
      icon: NotebookPen,
      label: T.strategy.tabs.playbook,
      desc: T.strategy.sectionDesc.playbook,
      access: T.strategy.access.everyone,
      staffOnly: false,
    },
  ]

  const current = sections.find(s => s.id === active) ?? sections[0]

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.strategy.pageTitle} />

      <div className="flex-1 overflow-y-auto">
        {/* ── Bölüm sekmeleri ───────────────────────────────────────────── */}
        {/* Cam bant: sitenin bölüm bandıyla aynı yüzey, panelin üst satırının
            altında ikinci bir hat olarak okunur. */}
        <div className="border-b border-white/10 bg-black/25 px-5 py-3 backdrop-blur-sm">
          <div className="flex flex-wrap gap-1.5" role="tablist">
            {sections.map(s => {
              const isActive = s.id === active
              const locked = s.staffOnly && !staff
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => navigate(sectionPath(s.id))}
                  className={`flex items-center gap-2 rounded-sm border px-3 py-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] transition-colors ${
                    isActive
                      ? 'border-primary/50 bg-primary/15 text-foreground'
                      : 'border-white/10 bg-white/[0.035] text-muted-foreground hover:border-white/25 hover:text-foreground'
                  }`}
                >
                  <s.icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {s.label}
                  {locked && <Lock className="h-3 w-3 shrink-0 opacity-50" />}
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Etkin bölüm ───────────────────────────────────────────────── */}
        <div className="p-5">
          {/* Bölüm başlığı: kızıl saç teli + display yüzü, panelin bölüm
              başlığı kalıbı. Erişim kuralı başlığın yanında durur — kim
              ekleyebilir sorusunun cevabı tıklandıktan sonra değil önce
              görünmeli. */}
          <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span aria-hidden className="h-px w-6 shrink-0 bg-primary" />
            <h2 className="font-display text-base font-bold uppercase tracking-[0.08em]">
              {current.label}
            </h2>
            <span className="flex items-center gap-1 rounded-sm border border-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
              {current.staffOnly ? <Lock className="h-2.5 w-2.5" /> : <Users className="h-2.5 w-2.5" />}
              {current.access}
            </span>
            <p className="w-full text-xs leading-relaxed text-muted-foreground sm:w-auto sm:flex-1">
              {current.desc}
            </p>
          </div>

          {isStrategyKind(active) ? (
            <StrategyLibrary
              key={active}
              kind={active}
              canCreate={active === 'playbook' || staff}
            />
          ) : (
            <PointsPicker />
          )}
        </div>
      </div>
    </div>
  )
}
